/**
 * Dropdown menus, replacing Bootstrap 3's jQuery plugin.
 *
 * Entirely delegated from document, so every `data-toggle="dropdown"` site
 * keeps working with no template change. The CSS contract
 * is unchanged: `.open > .dropdown-menu { display: block }` from
 * legacy/dropdowns.less does the showing; this only moves the `.open` class.
 *
 * Behaviour reproduced from bootstrap 3.4.1 js/dropdown.js:
 *   - the class goes on the toggle's `data-target` if it has one, else on its
 *     parent element
 *   - opening one closes any other
 *   - any document click closes them, including picking a menu item, except a
 *     click inside a `.dropdown form`, on an input or textarea inside the open
 *     menu, or anywhere on a menu item holding a checkbox (the pickers'
 *     "Scheduled ... only" option, whose whole row toggles it)
 *   - opening focuses the toggle, which Safari and Firefox don't do for a
 *     clicked button, so the keyboard handling below works after a mouse open
 *   - on touch screens, outside `.navbar-nav`, a `.dropdown-backdrop` covers the
 *     page while a menu is open, so a tap outside only closes it; iOS also
 *     doesn't deliver taps on non-clickable elements to document
 *   - right-clicks do not close
 *   - on a focused closed toggle, Up/Down/Space open the menu; once open,
 *     Up/Down move through its enabled items, and Escape closes it and
 *     returns focus to the toggle
 *
 * Neither a CSS-only (`:focus-within`) nor a native `popover` approach covers
 * that keyboard behaviour on the browsers this app targets, so it stays in JS.
 */

const TOGGLE = '[data-toggle="dropdown"]';
const OPEN = 'open';
const KEYS = ['Escape', 'ArrowUp', 'ArrowDown', ' '];

function parentOf(toggle) {
  const selector =
    toggle.getAttribute('data-target') ||
    (toggle.getAttribute('href') || '').replace(/.*(?=#[^\s]*$)/, '');
  if (selector && selector !== '#') {
    const found = document.querySelector(selector);
    if (found) return found;
  }
  return toggle.parentElement;
}

let backdrop = null;

// `keep`: a node whose open dropdown stays open (an input clicked inside it).
function closeAll(keep = null) {
  let kept = false;
  document.querySelectorAll(`.${OPEN}`).forEach((parent) => {
    const toggle = parent.querySelector(TOGGLE);
    if (!toggle) return; // some other component's .open
    if (keep && parent.contains(keep)) {
      kept = true;
      return;
    }
    parent.classList.remove(OPEN);
    toggle.setAttribute('aria-expanded', 'false');
  });
  if (backdrop && !kept) {
    backdrop.remove();
    backdrop = null;
  }
}

function itemsOf(parent) {
  return [...parent.querySelectorAll('.dropdown-menu li:not(.disabled) > a')].filter(
    (a) => a.offsetParent !== null,
  );
}

// What a click keeps open: Bootstrap kept the menu holding a clicked input or
// textarea open; a menu item with a checkbox is an option to toggle, so the
// whole row does the same.
function keptOpenBy(target) {
  if (/input|textarea/i.test(target.tagName)) return target;
  const item = target.closest('.dropdown-menu li');
  return item && item.querySelector('input[type="checkbox"]') ? item : null;
}

let wired = false;

export default function installDropdowns() {
  if (wired) return;
  wired = true;

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest(TOGGLE);

    if (toggle) {
      e.preventDefault();
      if (toggle.classList.contains('disabled') || toggle.disabled) return;
      const parent = parentOf(toggle);
      const isOpen = parent.classList.contains(OPEN);
      closeAll();
      if (!isOpen) {
        if ('ontouchstart' in document.documentElement && !parent.closest('.navbar-nav')) {
          backdrop = document.createElement('div');
          backdrop.className = 'dropdown-backdrop';
          // Its own listener makes it clickable for iOS.
          backdrop.addEventListener('click', () => closeAll());
          toggle.after(backdrop);
        }
        parent.classList.add(OPEN);
        toggle.setAttribute('aria-expanded', 'true');
        toggle.focus();
      }
      return;
    }

    // Bootstrap let clicks inside a form in a dropdown through without closing
    // it, so a menu can hold a filter box.
    if (e.target.closest('.dropdown form')) return;
    if (e.button === 2) return;
    closeAll(keptOpenBy(e.target));
  });

  // Bootstrap bound this to the toggle and to `.dropdown-menu`, and skipped
  // typing in an input or textarea; delegating from document keeps both.
  document.addEventListener('keydown', (e) => {
    if (!KEYS.includes(e.key) || /input|textarea/i.test(e.target.tagName)) return;
    const toggle = e.target.closest(TOGGLE);
    const menu = toggle ? null : e.target.closest('.dropdown-menu');
    if (!toggle && !menu) return;
    // Resolved whether or not it is open: a closed toggle still has to open.
    const parent = toggle ? parentOf(toggle) : menu.parentElement;
    if (!toggle && !parent.querySelector(TOGGLE)) return; // some other component's menu

    e.preventDefault();
    e.stopPropagation();
    if (toggle && (toggle.classList.contains('disabled') || toggle.disabled)) return;

    const isOpen = parent.classList.contains(OPEN);
    if (!isOpen && e.key !== 'Escape') {
      // Arrow or Space on a closed toggle opens it, as a click would.
      if (toggle) toggle.click();
      return;
    }
    if (e.key === 'Escape') {
      if (!isOpen) return;
      closeAll();
      const owner = toggle || parent.querySelector(TOGGLE);
      if (owner) owner.focus();
      return;
    }

    const items = itemsOf(parent);
    if (!items.length) return;
    let index = items.indexOf(e.target);
    if (e.key === 'ArrowUp' && index > 0) index -= 1;
    if (e.key === 'ArrowDown' && index < items.length - 1) index += 1;
    if (index === -1) index = 0;
    items[index].focus();
  });
}
